import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ErrorResponse } from 'legacy/common/utils/error';
import { useCursorQuery } from 'helpers/queries/useCursorQuery';
import {
  IncomingWorkflowCreationDto,
  IncomingWorkflowUpdateDto,
  OutgoingWorkflowListDto,
  OutgoingWorkflowDto,
  IncomingWorkflowSubmissionCreationDto,
  OutgoingWorkflowSubmissionListDto,
  OutgoingWorkflowSubmissionDto,
  IncomingWorkflowSubmissionReviewCreationDto,
  OutgoingWorkflowSubmissionReviewDto,
  WorkflowSubmissionsFilters
} from 'api/dtos/workflows/workflows';
import {
  getAssociationWorkflows,
  getAssociationWorkflowSubmissions,
  getAssociationWorkflow,
  createAssociationWorkflow,
  editAssociationWorkflow,
  deleteAssociationWorkflow,
  createAssociationWorkflowSubmission,
  getAssociationWorkflowSubmission,
  getMyWorkflowSubmissions,
  createAssociationWorkflowSubmissionReview
} from 'api/services/workflows/workflows';

// Query keys
export const associationWorkflowsKeys = {
  all: ['associationWorkflows'] as const,
  lists: (associationId: string) =>
    ['associationWorkflows', { associationId }] as const,
  detail: (associationId: string, workflowId: string) =>
    ['associationWorkflows', { associationId, workflowId }] as const,
  submissions: (associationId: string) =>
    ['associationWorkflowSubmissions', { associationId }] as const,
  submissionDetail: (
    associationId: string,
    workflowId: string,
    submissionId: string
  ) =>
    [
      'associationWorkflowSubmissions',
      { associationId, workflowId, submissionId }
    ] as const,
  mySubmissions: (associationId: string) =>
    ['myWorkflowSubmissions', { associationId }] as const
};

// Queries
export function useGetAssociationWorkflows(
  { associationId }: { associationId: string },
  options?: { enabled?: boolean }
) {
  return useQuery<OutgoingWorkflowListDto, ErrorResponse>(
    associationWorkflowsKeys.lists(associationId),
    () => getAssociationWorkflows(associationId),
    {
      enabled: associationId ? options?.enabled ?? true : false
    }
  );
}

export function useGetAssociationWorkflowSubmissions({
  associationId,
  numberOfItems,
  filters
}: {
  associationId: string;
  numberOfItems?: number;
  filters?: WorkflowSubmissionsFilters;
}) {
  const { data, ...rest } = useCursorQuery<OutgoingWorkflowSubmissionListDto>(
    ['associationWorkflowSubmissions', associationId, numberOfItems, filters],
    (_, fetchCursor) =>
      getAssociationWorkflowSubmissions({
        associationId,
        cursor: fetchCursor,
        numberOfItems,
        filters
      }),
    {
      keepPreviousData: true,
      select: (data) => ({
        ...data,
        // currentPage is 0-indexed, bumping it here to make things easier everywhere.
        currentPage: data.currentPage + 1
      })
    }
  );

  return {
    data: data || null,
    ...rest
  };
}

export function useGetAssociationWorkflow({
  associationId,
  workflowId
}: {
  associationId: string;
  workflowId: string;
}) {
  return useQuery<OutgoingWorkflowDto, ErrorResponse>(
    associationWorkflowsKeys.detail(associationId, workflowId),
    () => getAssociationWorkflow({ associationId, workflowId })
  );
}

export function useGetAssociationWorkflowSubmission({
  associationId,
  workflowId,
  submissionId
}: {
  associationId: string;
  workflowId: string;
  submissionId: string;
}) {
  return useQuery<OutgoingWorkflowSubmissionDto, ErrorResponse>(
    associationWorkflowsKeys.submissionDetail(
      associationId,
      workflowId,
      submissionId
    ),
    () =>
      getAssociationWorkflowSubmission({
        associationId,
        workflowId,
        submissionId
      })
  );
}

export function useGetMyWorkflowSubmissions({
  associationId,
  numberOfItems,
  filters
}: {
  associationId: string;
  numberOfItems?: number;
  filters?: WorkflowSubmissionsFilters;
}) {
  const { data, ...rest } = useCursorQuery<OutgoingWorkflowSubmissionListDto>(
    ['myWorkflowSubmissions', associationId, numberOfItems, filters],
    (_, fetchCursor) =>
      getMyWorkflowSubmissions({
        associationId,
        cursor: fetchCursor,
        numberOfItems,
        filters
      }),
    {
      keepPreviousData: true,
      select: (data) => ({
        ...data,
        // currentPage is 0-indexed, bumping it here to make things easier everywhere.
        currentPage: data.currentPage + 1
      })
    }
  );

  return {
    data: data || null,
    ...rest
  };
}

// Mutations
export function useCreateAssociationWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingWorkflowDto,
    ErrorResponse,
    {
      associationId: string;
      workflow: IncomingWorkflowCreationDto;
    }
  >(createAssociationWorkflow, {
    onSuccess: (_, { associationId }) => {
      queryClient.invalidateQueries(
        associationWorkflowsKeys.lists(associationId)
      );
    }
  });
}

export function useEditAssociationWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingWorkflowDto,
    ErrorResponse,
    {
      associationId: string;
      workflowId: string;
      workflow: IncomingWorkflowUpdateDto;
    }
  >(editAssociationWorkflow, {
    onSuccess: (_, { associationId, workflowId }) => {
      queryClient.invalidateQueries(
        associationWorkflowsKeys.lists(associationId)
      );
      queryClient.invalidateQueries(
        associationWorkflowsKeys.detail(associationId, workflowId)
      );
    }
  });
}

export function useDeleteAssociationWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    ErrorResponse,
    {
      associationId: string;
      workflowId: string;
    }
  >(deleteAssociationWorkflow, {
    onSuccess: (_, { associationId }) => {
      queryClient.invalidateQueries(
        associationWorkflowsKeys.lists(associationId)
      );
    }
  });
}

export function useCreateAssociationWorkflowSubmission() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingWorkflowSubmissionDto,
    ErrorResponse,
    {
      associationId: string;
      workflowId: string;
      submission: IncomingWorkflowSubmissionCreationDto;
    }
  >(createAssociationWorkflowSubmission, {
    onSuccess: (_, { associationId }) => {
      queryClient.invalidateQueries(
        associationWorkflowsKeys.submissions(associationId)
      );
      queryClient.invalidateQueries(
        associationWorkflowsKeys.mySubmissions(associationId)
      );
    }
  });
}

export function useCreateAssociationWorkflowSubmissionReview() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingWorkflowSubmissionReviewDto,
    ErrorResponse,
    {
      associationId: string;
      workflowId: string;
      submissionId: string;
      review: IncomingWorkflowSubmissionReviewCreationDto;
    }
  >(createAssociationWorkflowSubmissionReview, {
    onSuccess: (_, { associationId, workflowId, submissionId }) => {
      queryClient.invalidateQueries(
        associationWorkflowsKeys.submissions(associationId)
      );
      queryClient.invalidateQueries(
        associationWorkflowsKeys.mySubmissions(associationId)
      );
      queryClient.invalidateQueries(
        associationWorkflowsKeys.submissionDetail(
          associationId,
          workflowId,
          submissionId
        )
      );
    }
  });
}
